import React from 'react';
import { VideoEvidenceData, BodyLanguageData } from '../types';
import { useTranslation } from '../../../i18n';

interface VideoObservationsCardProps {
  evidence?: (VideoEvidenceData & {
    analysisJobId?: string | null;
    provenance?: {
      eventCount?: number;
      analysisVersion?: string;
      processingDurationSeconds?: number;
      effectiveConfig?: any;
      warnings?: string[];
    } | null;
    errorReason?: string | null;
    metrics?: any;
    reason?: string | null;
  }) | null;
  bodyLanguage?: (BodyLanguageData & {
    analysisJobId?: string | null;
    provenance?: {
      eventCount?: number;
      analysisVersion?: string;
      processingDurationSeconds?: number;
      warnings?: string[];
    } | null;
    metrics?: any;
    reason?: string | null;
  }) | null;
  requiresCamera?: boolean;
  isAudioOnly?: boolean;
  isLoading?: boolean;
  isAdmin?: boolean;
  videoAnalysisEnabled?: boolean;
}

export const VideoObservationsCard: React.FC<VideoObservationsCardProps> = ({
  evidence,
  bodyLanguage,
  requiresCamera,
  isAudioOnly = false,
  isLoading = false,
  isAdmin = false,
  videoAnalysisEnabled,
}) => {
  const { t } = useTranslation();
  const rawStatus = (evidence?.status || bodyLanguage?.status || 'UNAVAILABLE').toUpperCase();

  const isScenarioAudioOnly = Boolean(
    isAudioOnly ||
    requiresCamera === false ||
    videoAnalysisEnabled === false ||
    rawStatus === 'DISABLED' ||
    rawStatus === 'NOT_ENABLED' ||
    rawStatus === 'AUDIO_ONLY' ||
    rawStatus === 'NOT_REQUIRED'
  );

  // If simulation is audio only, camera is not required, or video analysis is not active: hide completely
  if (isScenarioAudioOnly) {
    return null;
  }

  if (isLoading) {
    return (
      <div style={{ background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '12px', padding: 'clamp(14px, 3vw, 20px)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
          <h3 style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--faint)', margin: 0 }}>
            {t('scorecard.camera_analysis_title', undefined, 'Camera Video Analysis')}
          </h3>
          <span className="admin-status-badge admin-status-badge--pending">
            {t('common.loading', undefined, 'Loading...')}
          </span>
        </div>
        <p style={{ fontSize: '12.5px', color: 'var(--muted)', margin: 0 }}>
          {t('scorecard.retrieving_telemetry', undefined, 'Retrieving video evidence telemetry...')}
        </p>
      </div>
    );
  }

  const rawMetrics = evidence?.metrics || bodyLanguage?.metrics || {};

  // 1. Core Configurable Metrics with Zero Invented Defaults
  const faceVisiblePercentage = typeof rawMetrics.faceVisiblePercentage === 'number'
    ? rawMetrics.faceVisiblePercentage
    : null;

  const faceCenteredPercentage = typeof rawMetrics.faceCenteredPercentage === 'number'
    ? rawMetrics.faceCenteredPercentage
    : null;

  const validSampledFrames = typeof rawMetrics.validSampledFrames === 'number'
    ? rawMetrics.validSampledFrames
    : (typeof rawMetrics.sampleCount === 'number' ? rawMetrics.sampleCount : 0);

  const failedSamplesCount = typeof rawMetrics.failedSamplesCount === 'number'
    ? rawMetrics.failedSamplesCount
    : 0;

  const averageConfidence = typeof rawMetrics.averageConfidence === 'number'
    ? rawMetrics.averageConfidence
    : null;

  const noFacePeriodsCount = typeof rawMetrics.faceMissingEventsCount === 'number'
    ? rawMetrics.faceMissingEventsCount
    : (typeof rawMetrics.noFacePeriodsCount === 'number' ? rawMetrics.noFacePeriodsCount : 0);

  const missingPeriods: Array<{
    startOffsetMs: number;
    endOffsetMs: number;
    durationMs: number;
    warningIssued?: boolean;
  }> = Array.isArray(rawMetrics.missingPeriods) ? rawMetrics.missingPeriods : [];

  const headPoseSummary = rawMetrics.headPoseSummary || null;
  const expressionsSummary = rawMetrics.expressionsSummary || null;
  const approximateExpressions = rawMetrics.approximateExpressions || null;

  const jobId = evidence?.analysisJobId || bodyLanguage?.analysisJobId;
  const provenance = evidence?.provenance || bodyLanguage?.provenance;
  const warnings = provenance?.warnings || [];

  // Format offset in minutes:seconds
  const formatOffset = (ms: number) => {
    const totalSec = Math.floor(ms / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // State 2: PROCESSING / PENDING
  if ((rawStatus === 'PROCESSING' || rawStatus === 'PENDING' || rawStatus === 'INITIALIZING') && faceVisiblePercentage === null) {
    return (
      <div style={{ background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '12px', padding: 'clamp(14px, 3vw, 20px)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
          <h3 style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--faint)', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: 'var(--blue)', animation: 'pulse 1.5s infinite' }} />
            {t('scorecard.camera_analysis_title', undefined, 'Camera Video Analysis')}
          </h3>
          <span className="admin-status-badge admin-status-badge--pending">
            {t('scorecard.video_processing', undefined, 'Processing')}
          </span>
        </div>
        <p style={{ fontSize: '12.5px', color: 'var(--ink)', margin: '0 0 6px 0', fontWeight: 500 }}>
          {t('scorecard.video_processing_sub', undefined, 'Video analysis is currently processing.')}
        </p>
        <p style={{ fontSize: '12px', color: 'var(--muted)', margin: 0 }}>
          {t('scorecard.cv_observations_sub', undefined, 'Observations will appear once on-device telemetry is finalized.')}
        </p>
      </div>
    );
  }

  // State 3 & 4: If video analysis failed, was not active, or has zero valid samples: hide completely
  const isAvailable = Boolean(
    (rawStatus === 'COMPLETED' || rawStatus === 'PARTIAL' || rawStatus === 'AVAILABLE') &&
    (validSampledFrames > 0 && (faceVisiblePercentage !== null || faceCenteredPercentage !== null))
  );

  if (!isAvailable) {
    return null;
  }

  // State 5: COMPLETED / PARTIAL with Grounded Observational Metrics
  return (
    <div className="scorecard-card" style={{ background: 'var(--white)', border: '1px solid var(--line)', borderRadius: '12px', padding: 'clamp(14px, 3vw, 20px)', display: 'grid', gap: '16px', minWidth: 0, width: '100%', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', borderBottom: '1px solid var(--line)', paddingBottom: '12px' }}>
        <div>
          <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--navy)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg style={{ width: '16px', height: '16px', color: 'var(--blue)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            {t('scorecard.camera_analysis_title', undefined, 'Camera Video Analysis')} (face-api.js)
          </h3>
          <p style={{ fontSize: '12px', color: 'var(--muted)', margin: '2px 0 0' }}>
            {t('scorecard.cv_observations_sub', undefined, 'Objective physical observations derived from on-device computer vision')}
          </p>
        </div>
        <span className={`admin-status-badge ${rawStatus === 'PARTIAL' ? 'admin-status-badge--pending' : 'admin-status-badge--active'}`} style={{
          background: rawStatus === 'PARTIAL' ? 'var(--warn-soft)' : 'var(--ok-soft)',
          color: rawStatus === 'PARTIAL' ? 'var(--warn)' : 'var(--ok)'
        }}>
          {rawStatus === 'PARTIAL' ? t('scorecard.partial_analysis', undefined, 'Partial Analysis') : t('scorecard.analysis_completed', undefined, 'Analysis Completed')}
        </span>
      </div>

      {/* Primary Metrics Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' }}>
        {/* Metric 1: Face Visibility */}
        <div style={{ background: 'var(--surface)', padding: '12px', borderRadius: '8px', border: '1px solid var(--line)' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', display: 'block' }}>
            {t('scorecard.face_visible', undefined, 'Face Visible')}
          </span>
          <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--navy)', marginTop: '2px', display: 'block' }}>
            {faceVisiblePercentage !== null ? `${faceVisiblePercentage}%` : 'Unavailable'}
          </span>
          <span style={{ fontSize: '10.5px', color: 'var(--muted)' }}>
            {noFacePeriodsCount > 0 ? t('scorecard.missing_intervals', { count: noFacePeriodsCount }, `${noFacePeriodsCount} missing intervals`) : t('scorecard.continuous_presence', undefined, 'Continuous presence')}
          </span>
        </div>

        {/* Metric 2: Face Centering (Framing) */}
        <div style={{ background: 'var(--surface)', padding: '12px', borderRadius: '8px', border: '1px solid var(--line)' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', display: 'block' }}>
            {t('scorecard.face_centering', undefined, 'Face Centering')}
          </span>
          <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--navy)', marginTop: '2px', display: 'block' }}>
            {faceCenteredPercentage !== null
              ? `${faceCenteredPercentage}%`
              : (faceVisiblePercentage !== null && faceVisiblePercentage >= 70 ? 'In Frame' : 'Not Tracked')}
          </span>
          <span style={{ fontSize: '10.5px', color: 'var(--muted)' }}>
            {faceCenteredPercentage !== null
              ? t('scorecard.framing_alignment', undefined, 'Framing zone alignment')
              : (faceVisiblePercentage !== null && faceVisiblePercentage >= 70 ? t('scorecard.optimal_positioning', undefined, 'Optimal camera positioning') : t('scorecard.framing_omitted', undefined, 'Framing telemetry omitted'))}
          </span>
        </div>

        {/* Metric 3: Sampling Quality & Volume */}
        <div style={{ background: 'var(--surface)', padding: '12px', borderRadius: '8px', border: '1px solid var(--line)' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', display: 'block' }}>
            {t('scorecard.sampled_frames', undefined, 'Sampled Frames')}
          </span>
          <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--navy)', marginTop: '2px', display: 'block' }}>
            {validSampledFrames}
          </span>
          <span style={{ fontSize: '10.5px', color: failedSamplesCount > 0 ? 'var(--warn)' : 'var(--muted)' }}>
            {failedSamplesCount > 0 ? t('scorecard.failed_samples', { count: failedSamplesCount }, `${failedSamplesCount} failed samples`) : t('scorecard.valid_samples', undefined, '100% valid samples')}
          </span>
        </div>

        {/* Metric 4: Average Model Confidence */}
        <div style={{ background: 'var(--surface)', padding: '12px', borderRadius: '8px', border: '1px solid var(--line)' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--muted)', display: 'block' }}>
            {t('scorecard.model_confidence', undefined, 'Model Confidence')}
          </span>
          <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--navy)', marginTop: '2px', display: 'block' }}>
            {averageConfidence !== null ? `${Math.round(averageConfidence * 100)}%` : 'N/A'}
          </span>
          <span style={{ fontSize: '10.5px', color: 'var(--muted)' }}>
            {t('scorecard.detection_certainty', undefined, 'Detection certainty')}
          </span>
        </div>
      </div>

      {/* Section A: No-Face Intervals Breakdown */}
      {missingPeriods.length > 0 && (
        <div style={{ borderTop: '1px solid var(--line)', paddingTop: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--faint)' }}>
              {t('scorecard.no_face_intervals', { count: missingPeriods.length }, `No-Face Intervals (${missingPeriods.length})`)}
            </span>
            <span style={{ fontSize: '10.5px', color: 'var(--muted)' }}>
              {t('scorecard.no_face_desc', undefined, 'Intervals when learner was out of frame')}
            </span>
          </div>
          <div style={{ display: 'grid', gap: '6px', maxHeight: '140px', overflowY: 'auto' }}>
            {missingPeriods.map((period, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '6px 10px',
                  background: period.warningIssued ? 'var(--warn-soft)' : 'var(--surface)',
                  border: `1px solid ${period.warningIssued ? 'rgba(178, 106, 0, 0.2)' : 'var(--line)'}`,
                  borderRadius: '6px',
                  fontSize: '12px',
                }}
              >
                <span style={{ fontWeight: 500, color: period.warningIssued ? 'var(--warn)' : 'var(--ink)' }}>
                  {t('scorecard.interval_num', { num: idx + 1, start: formatOffset(period.startOffsetMs), end: formatOffset(period.endOffsetMs) }, `Interval #${idx + 1}: ${formatOffset(period.startOffsetMs)} – ${formatOffset(period.endOffsetMs)}`)}
                </span>
                <span style={{ fontSize: '11.5px', color: 'var(--muted)' }}>
                  {t('scorecard.duration_sec', { sec: Math.round(period.durationMs / 1000) }, `${Math.round(period.durationMs / 1000)}s duration`)}
                  {period.warningIssued && <strong style={{ color: 'var(--warn)', marginLeft: '6px' }}>{t('scorecard.warning_tag', undefined, '(Warning)')}</strong>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section B: Optional Head-Pose Observations (when supported and configured) */}
      {headPoseSummary && (
        <div style={{ borderTop: '1px solid var(--line)', paddingTop: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--faint)' }}>
              {t('scorecard.head_pose_title', undefined, 'Head-Pose Orientation Observations')}
            </span>
            <span style={{ fontSize: '10px', color: headPoseSummary.supported ? 'var(--ok)' : 'var(--muted)', background: 'var(--surface)', padding: '2px 6px', borderRadius: '4px' }}>
              {headPoseSummary.supported ? t('scorecard.supported', undefined, 'Supported') : t('scorecard.unsupported', undefined, 'Unsupported')}
            </span>
          </div>
          {headPoseSummary.supported ? (
            <div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <div style={{ background: 'var(--surface)', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--line)', fontSize: '12px' }}>
                  <span style={{ color: 'var(--muted)', display: 'block', fontSize: '10.5px' }}>{t('scorecard.within_limits', undefined, 'Within Limits')}</span>
                  <strong style={{ color: 'var(--navy)' }}>{headPoseSummary.withinThresholdPercentage}%</strong>
                </div>
                <div style={{ background: 'var(--surface)', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--line)', fontSize: '12px' }}>
                  <span style={{ color: 'var(--muted)', display: 'block', fontSize: '10.5px' }}>{t('scorecard.avg_angles', undefined, 'Avg Yaw / Pitch / Roll')}</span>
                  <strong style={{ color: 'var(--navy)' }}>{headPoseSummary.averageYaw}° / {headPoseSummary.averagePitch}° / {headPoseSummary.averageRoll}°</strong>
                </div>
                <div style={{ background: 'var(--surface)', padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--line)', fontSize: '12px' }}>
                  <span style={{ color: 'var(--muted)', display: 'block', fontSize: '10.5px' }}>{t('scorecard.max_rotation', undefined, 'Max Rotation')}</span>
                  <strong style={{ color: 'var(--navy)' }}>Yaw: {headPoseSummary.maxYaw}°, Pitch: {headPoseSummary.maxPitch}°</strong>
                </div>
              </div>
              <p style={{ margin: '8px 0 0', fontSize: '11px', color: 'var(--muted)', fontStyle: 'italic', lineHeight: 1.4 }}>
                Notice: {headPoseSummary.note}
              </p>
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: '11.5px', color: 'var(--muted)', fontStyle: 'italic' }}>
              {t('scorecard.head_pose_unsupported', undefined, 'Head-pose estimation is unsupported or landmark angles were unavailable from the detector.')}
            </p>
          )}
        </div>
      )}

      {/* Section C: Optional Approximate Expressions Summary (face-api.js) */}
      {(expressionsSummary || (Array.isArray(approximateExpressions) && approximateExpressions.length > 0)) && (
        <div style={{ borderTop: '1px solid var(--line)', paddingTop: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--faint)' }}>
              {t('scorecard.facial_expressions_title', undefined, 'Approximate Facial Expressions (face-api.js)')}
            </span>
            <span style={{ fontSize: '10px', color: 'var(--muted)', background: 'var(--surface)', padding: '2px 6px', borderRadius: '4px' }}>
              {t('scorecard.on_device_outputs', undefined, 'On-Device Model Outputs')}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {expressionsSummary
              ? Object.entries(expressionsSummary)
                  .filter(([_, count]: any) => typeof count === 'number' && count > 0)
                  .map(([expr, count]: any) => (
                    <div
                      key={expr}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        background: 'var(--surface)',
                        border: '1px solid var(--line)',
                        fontSize: '12px',
                        textTransform: 'capitalize',
                      }}
                    >
                      <span style={{ fontWeight: 600, color: 'var(--ink)' }}>{expr}</span>
                      <span style={{ color: 'var(--muted)', fontSize: '11px' }}>({count} samples)</span>
                    </div>
                  ))
              : approximateExpressions?.map((expr: any) => (
                  <div
                    key={expr.expression}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 10px',
                      borderRadius: '6px',
                      background: 'var(--surface)',
                      border: '1px solid var(--line)',
                      fontSize: '12px',
                      textTransform: 'capitalize',
                    }}
                  >
                    <span style={{ fontWeight: 600, color: 'var(--ink)' }}>{expr.expression}</span>
                    <span style={{ color: 'var(--muted)', fontSize: '11px' }}>({expr.percentage}%)</span>
                  </div>
                ))}
          </div>
          <p style={{ margin: '8px 0 0', fontSize: '11px', color: 'var(--muted)', fontStyle: 'italic', lineHeight: 1.4 }}>
            {t('scorecard.facial_notice', undefined, 'Notice: Facial expression labels are approximate local model observations. They are separate from dialogue evaluation and do not directly increase or decrease competency scores.')}
          </p>
        </div>
      )}

      {/* Section D: Observational Quality Notice & Disclaimers */}
      <div style={{ borderTop: '1px solid var(--line)', paddingTop: '10px' }}>
        <p style={{ margin: 0, fontSize: '11px', color: 'var(--muted)', lineHeight: 1.5 }}>
          {t('scorecard.assessment_integrity_notice', undefined, 'Assessment Integrity Notice: Physical camera metrics reflect camera framing and on-device detection confidence. They are provided as observational context only and do not alter conversational competency scores.')}
        </p>
      </div>

      {/* Admin Diagnostic Info */}
      {isAdmin && (jobId || provenance || warnings.length > 0) && (
        <div style={{ borderTop: '1px dashed var(--line)', paddingTop: '10px', marginTop: '4px', fontSize: '11px', color: 'var(--muted)', display: 'grid', gap: '4px' }}>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {jobId && <div>Job: <span style={{ fontFamily: 'monospace' }}>{jobId}</span></div>}
            {provenance?.analysisVersion && <div>Model: {provenance.analysisVersion}</div>}
            {provenance?.eventCount !== undefined && <div>Events: {provenance.eventCount}</div>}
          </div>
          {warnings.length > 0 && (
            <div style={{ color: 'var(--warn)', fontSize: '10.5px' }}>
              Warnings: {warnings.slice(0, 3).join(' • ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
